import type { BaseChannel, MetaData, ThreadInfoUpdateEvent, User, UserMessage } from '@sendbird/chat/lib/__definition';
import type { OpenChannel, SendbirdOpenChat } from '@sendbird/chat/openChannel';
import type { ReactNode } from 'react';

import { usePrevious } from '@movingimage-evp/mi-ui-component-library';
import SendbirdChat, { ConnectionHandler } from '@sendbird/chat';
import { OpenChannelHandler, OpenChannelModule } from '@sendbird/chat/openChannel';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as generateId } from 'uuid';

import { ChatContext } from './chat-context';
import {
  type Action,
  type ChatConfig,
  type ChatMessage,
  MessageCustomType,
  type Reaction,
  type ReactionKey,
} from './chat.types';
import { getCustomTypes } from './get-custom-types';
import { setUserInformation } from '../../utils/user-information-utils';

export function ChatProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  const sendbirdRef = useRef<SendbirdOpenChat>(undefined);
  const channelRef = useRef<OpenChannel>(undefined);
  const errorTimeoutRef = useRef<number>(undefined);

  const [user, setUser] = useState<User>();
  const [metadata, setMetadata] = useState<MetaData>({});
  const [connected, setConnected] = useState(false);
  const [connectedAt, setConnectedAt] = useState(0);
  const [connecting, setConnecting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [editingMessage, setEditingMessage] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(false);
  const [userActionError, setUserActionError] = useState<string>();
  const [providerError, setProviderError] = useState<string>();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const messageLength = messages.length;
  const previousMessagesLength = usePrevious(messages.length);

  useEffect(() => {
    if (messageLength > previousMessagesLength) setHasNewMessages(true);
  }, [messageLength, previousMessagesLength]);

  const [moderatorReplies, setModeratorReplies] = useState<ChatMessage[]>([]);
  const [readRepliesIds, setReadRepliesIds] = useState<ChatMessage['messageId'][]>([]);

  const repliesForCurrentUser = moderatorReplies.filter(
    (reply) => (reply.parentMessage as UserMessage).sender.userId === user?.userId
  );

  const newModeratorReplies = repliesForCurrentUser.filter((reply) => connectedAt > 0 && reply.createdAt > connectedAt);
  const unreadCount = newModeratorReplies.filter((reply) => !readRepliesIds.includes(reply.messageId)).length;

  const latestUnreadModeratorReply = newModeratorReplies
    .filter((reply) => !readRepliesIds.includes(reply.messageId))
    .sort((a, b) => b.createdAt - a.createdAt)[0];

  const isReplyUnread = (messageId: ChatMessage['messageId']) =>
    (newModeratorReplies.find((reply) => reply.messageId === messageId) && !readRepliesIds.includes(messageId)) ||
    false;

  let actions: Action[] = [];

  try {
    actions = Object.entries(metadata).map(([id, value]) => {
      const { type, userId, messageId } = JSON.parse(value);
      return { id, type, userId, messageId };
    });
  } catch (error) {
    console.error(error);
  }

  const createChat = async ({
    chatUserId,
    nickname,
    applicationId,
    channelUrl,
    eventId,
    apiHost,
    wsHost,
  }: ChatConfig) => {
    if (window.Cypress) {
      setConnected(true);
      setConnectedAt(Date.now());
      return;
    }

    if (providerError) return;

    if (!applicationId || !channelUrl) return setProviderError(t('common.chat.errors.missingConfiguration'));

    setConnecting(true);

    const connectionHandler = new ConnectionHandler({
      onReconnectSucceeded: async () => {
        channelRef.current?.refresh();
        await loadAllMessages();
      },
    });

    const chat = SendbirdChat.init({
      appId: applicationId,
      modules: [new OpenChannelModule()],
      customApiHost: apiHost,
      customWebSocketHost: wsHost,
    });

    chat.addConnectionHandler('main-connection-handler', connectionHandler);
    sendbirdRef.current = chat as SendbirdOpenChat;

    try {
      await chat.connect(chatUserId);
    } catch {
      setProviderError(t('common.chat.errors.cannotConnectToChat'));
      return setConnecting(false);
    }

    chat.openChannel.addOpenChannelHandler(
      'main-handler',
      new OpenChannelHandler({
        onMessageReceived: (_, newMessage) => {
          if (!newMessage.parentMessage) setMessages((state) => [...state, newMessage as ChatMessage]);
        },
        onMessageUpdated: (_, updatedMessage) => {
          setMessages((state) =>
            state.map((message) =>
              message.messageId === updatedMessage.messageId ? (updatedMessage as ChatMessage) : message
            )
          );
        },
        onThreadInfoUpdated: async (channel: BaseChannel, threadInfoUpdateEvent: ThreadInfoUpdateEvent) => {
          const updatedMessage = (await chat.message.getMessage({
            messageId: threadInfoUpdateEvent.targetMessageId,
            channelType: channel.channelType,
            channelUrl: channel.url,
            includeThreadInfo: true,
          })) as ChatMessage;

          setMessages((state) =>
            state.map((existingMessage) =>
              existingMessage.messageId === updatedMessage.messageId ? updatedMessage : existingMessage
            )
          );
        },
        onMessageDeleted: (_, messageId) => {
          setMessages((state) => state.filter((item) => item.messageId !== messageId));
        },
        onMetaDataCreated(_, metaData) {
          setMetadata((state) => ({ ...state, ...metaData }));
        },
        onMetaDataDeleted(_, metaDataKeys) {
          metaDataKeys.forEach((key) => {
            setMetadata((state) => {
              const { [key]: _, ...newState } = state;
              return newState;
            });
          });
        },
      })
    );

    const user = chat.currentUser;

    let channel;
    try {
      channel = await chat.openChannel.getChannel(channelUrl);
    } catch {
      setProviderError(t('common.chat.errors.cannotConnectToChannel'));
      return setConnecting(false);
    }

    channelRef.current = channel;

    if (nickname) {
      setUserInformation(eventId, { nickname });
      await chat.updateCurrentUserInfo({ nickname });
    }

    try {
      await channel.enter();
    } catch {
      setProviderError(t('common.chat.errors.cannotConnectToChannel'));
      return setConnecting(false);
    }

    if (user) setUser(user);
    await loadAllMessages();
    setMetadata(await channel.getAllMetaData());
    setConnecting(false);
    setConnected(true);
    setConnectedAt(Date.now());
  };

  const loadAllMessages = async () => {
    const messageList: ChatMessage[] = [];
    const messageQuery = channelRef.current?.createPreviousMessageListQuery({ includeThreadInfo: true });

    async function fetchMessages() {
      if (!messageQuery || messageQuery.hasNext === false) return;

      messageList.push(...((await messageQuery.load()) as ChatMessage[]));

      await fetchMessages();
    }

    await fetchMessages();

    setMessages(messageList);
  };

  const connectUser = (config: ChatConfig) => {
    setUserInformation(config.eventId, { chatUserId: config.chatUserId });
    createChat(config);
  };

  const disconnectChat = () => {
    if (connected) {
      sendbirdRef.current?.disconnect();
    }

    setConnected(false);
    setConnectedAt(0);
    setProviderError(undefined);
    setUser(undefined);
    setMessages([]);
    setMetadata({});
    setUserActionError(undefined);
    setSendingMessage(false);
    setEditingMessage(false);
    setDeletingMessage(false);
    setConnecting(false);
  };

  const connectModerator = (config: ChatConfig) => {
    if (!config.chatUserId) return setProviderError(t('common.chat.errors.noModeratorIds'));

    createChat(config);
  };

  const sendMessage = (message: ChatMessage['message'], customTypes?: MessageCustomType[]) =>
    new Promise<boolean>((resolve) => {
      if (!customTypes && !message.trim()) return resolve(false);

      setSendingMessage(true);

      channelRef.current
        ?.sendUserMessage({ message, customType: customTypes?.join(',') })
        .onSucceeded((message) => {
          setMessages((state) => [...state, message as ChatMessage]);
          setSendingMessage(false);
          resolve(true);
        })
        .onFailed(() => {
          setUserActionError(t('common.chat.errors.sendMessage'));
          setSendingMessage(false);
          resolve(false);
        });
    });

  const sendThreadMessage = (
    parentMessageId: number,
    message: ChatMessage['message'],
    customTypes?: MessageCustomType[]
  ) =>
    new Promise<ChatMessage | undefined>((resolve) => {
      if (!message.trim()) return resolve(undefined);

      channelRef.current
        ?.sendUserMessage({ message, parentMessageId, customType: customTypes?.join(',') })
        .onSucceeded((message) => resolve(message as ChatMessage))
        .onFailed(() => {
          setUserActionError(t('common.chat.errors.sendThreadMessage'));
          resolve(undefined);
        });
    });

  const editMessage = async (
    messageId: ChatMessage['messageId'],
    message?: ChatMessage['message'],
    customTypes?: MessageCustomType[]
  ) => {
    if (!customTypes && !message?.trim()) return false;

    const handleSuccess = (updatedMessage: ChatMessage) => {
      setMessages((state) => state.map((item) => (item.messageId === messageId ? updatedMessage : item)));
      setEditingMessage(false);
      return true;
    };

    const handleError = () => {
      setUserActionError(t('common.chat.errors.editMessage'));
      setEditingMessage(false);
      return false;
    };

    setEditingMessage(true);

    try {
      const updatedMessage = await channelRef.current?.updateUserMessage(messageId, {
        message,
        customType: customTypes?.join(','),
      });
      return updatedMessage ? handleSuccess(updatedMessage as ChatMessage) : handleError();
    } catch {
      return handleError();
    }
  };

  const deleteMessage = async (message: ChatMessage) => {
    setDeletingMessage(true);

    try {
      await channelRef.current?.deleteMessage(message);
      setDeletingMessage(false);
      return true;
    } catch {
      setUserActionError(t('common.chat.errors.deleteMessage'));
      setDeletingMessage(false);
      return false;
    }
  };

  const approveMessage = (message: ChatMessage) => {
    const { types } = getCustomTypes(message)
      .remove(MessageCustomType.DECLINED_BY_MODERATOR)
      .add(MessageCustomType.APPROVED_BY_MODERATOR);

    return editMessage(message.messageId, undefined, types);
  };

  const declineMessage = (message: ChatMessage) => {
    const { types } = getCustomTypes(message)
      .remove(MessageCustomType.APPROVED_BY_MODERATOR)
      .add(MessageCustomType.DECLINED_BY_MODERATOR);

    return editMessage(message.messageId, undefined, types);
  };

  const answerMessage = (message: ChatMessage) => {
    const { types } = getCustomTypes(message).add(MessageCustomType.ANSWERED);

    return editMessage(message.messageId, undefined, types);
  };

  const unanswerMessage = (message: ChatMessage) => {
    const { types } = getCustomTypes(message).remove(MessageCustomType.ANSWERED);

    return editMessage(message.messageId, undefined, types);
  };

  const reactToMessage = (messageId: ChatMessage['messageId'], type: ReactionKey) => {
    if (!user) return;

    const existingReaction = actions.find(
      (action) => action.userId === user.userId && action.messageId === messageId && action.type === type
    );

    if (existingReaction) {
      channelRef.current?.deleteMetaData(existingReaction.id);
    } else {
      channelRef.current?.createMetaData({
        [generateId()]: JSON.stringify({ type, userId: user.userId, messageId }),
      });
    }
  };

  const getReactionsByMessageId = (id: ChatMessage['messageId']): Reaction => {
    const reactions = actions.filter(({ type, messageId }) => type !== 'vote' && messageId === id);

    return {
      heart: reactions.filter(({ type }) => type === 'heart').map(({ userId }) => userId),
      laugh: reactions.filter(({ type }) => type === 'laugh').map(({ userId }) => userId),
      thumbsUp: reactions.filter(({ type }) => type === 'thumbsUp').map(({ userId }) => userId),
    };
  };

  const getNumberOfReactionsByMessageId = (messageId: ChatMessage['messageId'], type: ReactionKey) => {
    return actions.filter((action) => action.messageId === messageId && action.type === type).length;
  };

  const hasUserReactedToMessage = (messageId: ChatMessage['messageId'], type: ReactionKey) => {
    return actions.some(
      (action) => action.userId === user?.userId && action.messageId === messageId && action.type === type
    );
  };

  const voteForMessage = (message: ChatMessage) => {
    if (!user) return;

    const messageId = message.messageId;

    const existingVote = actions.find(
      (action) => action.userId === user.userId && action.messageId === messageId && action.type === 'vote'
    );

    if (existingVote) channelRef.current?.deleteMetaData(existingVote.id);
    else
      channelRef.current?.createMetaData({
        [generateId()]: JSON.stringify({ type: 'vote', userId: user.userId, messageId }),
      });
  };

  const getVotesByMessageId = (messageId: ChatMessage['messageId']) => {
    return actions.filter((action) => action.messageId === messageId && action.type === 'vote').length;
  };

  const hasUserVotedForMessage = (messageId: ChatMessage['messageId']) => {
    return actions.some(
      (action) => action.userId === user?.userId && action.messageId === messageId && action.type === 'vote'
    );
  };

  // Clear user errors after 3 seconds
  useEffect(() => {
    if (userActionError) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = window.setTimeout(() => setUserActionError(undefined), 3000);
    }
  }, [userActionError]);

  return (
    <ChatContext.Provider
      value={{
        actions,
        user,
        messages: messages.sort((a, b) => a.createdAt - b.createdAt),
        connected,
        connectedAt,
        connecting,
        providerError,
        userActionError,
        sendingMessage,
        editingMessage,
        deletingMessage,
        unreadCount,
        hasNewMessages: hasNewMessages || unreadCount > 0,
        readRepliesIds,
        latestUnreadModeratorReply,
        isReplyUnread,
        setModeratorReplies,
        setReadRepliesIds,
        setHasNewMessages,
        connectUser,
        disconnectChat,
        connectModerator,
        sendMessage,
        sendThreadMessage,
        editMessage,
        deleteMessage,
        approveMessage,
        declineMessage,
        answerMessage,
        unanswerMessage,
        reactToMessage,
        getReactionsByMessageId,
        getNumberOfReactionsByMessageId,
        hasUserReactedToMessage,
        voteForMessage,
        getVotesByMessageId,
        hasUserVotedForMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
